/**
 * Bollinger Bands Quantum — motor puro de cálculo.
 *
 * Sin React ni lightweight-charts: entran velas OHLC + config y sale el
 * result completo (puntos de banda, derivados, estado, toques, último evento
 * y tramos de squeeze). Toda la validación ocurre ANTES de calcular; con
 * input insuficiente se devuelve un result vacío válido, nunca se lanza.
 *
 * Criterio de ventana idéntico al `sma()` del bollinger legacy
 * (frontend/src/indicators/bollinger/index.js): un valor inválido ROMPE la
 * ventana — no hay banda hasta reconstruir una ventana completa de `period`
 * valores válidos. stdDev POBLACIONAL: sqrt(sq / period).
 */

import { resolveConfig } from './constants.js'

function num(v) {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : null
}

/** Precio seleccionado por priceType; null si falta algún componente. */
function selectedPrice(candle, priceType) {
  if (!candle) return null
  const c = num(candle.close)
  if (priceType === 'close') return c
  const h = num(candle.high)
  const l = num(candle.low)
  if (priceType === 'hl2') {
    return h != null && l != null ? (h + l) / 2 : null
  }
  if (priceType === 'hlc3') {
    return h != null && l != null && c != null ? (h + l + c) / 3 : null
  }
  // ohlc4
  const o = num(candle.open)
  return o != null && h != null && l != null && c != null ? (o + h + l + c) / 4 : null
}

function emptyResult() {
  return {
    points: [],
    state: null,
    bandwidth: null,
    percentB: null,
    upperTouches: 0,
    lowerTouches: 0,
    lastEvent: null,
    squeezeSegments: [],
  }
}

/**
 * @param {Candle[]} candles
 * @param {Object} rawConfig config cruda (se resuelve aquí dentro)
 * @returns {BollingerResult}
 */
export function compute(candles, rawConfig) {
  const cfg = resolveConfig(rawConfig)
  if (cfg.enabled === false) return emptyResult()
  if (!Array.isArray(candles)) return emptyResult()

  const period = cfg.period
  const mult = cfg.multiplier

  // Validación previa (spec 3.7): sin period + 1 velas no hay cálculo.
  if (candles.length < period + 1) return emptyResult()

  const prices = candles.map(c => selectedPrice(c, cfg.priceType))
  const closes = candles.map(c => num(c?.close))

  // ── SMA + stdDev poblacional con ruptura de ventana ──────────────────────
  // Un valor inválido rompe la ventana (criterio del sma() legacy): solo hay
  // banda cuando las últimas `period` velas tienen precio válido.
  const points = []
  let streak = 0
  for (let i = 0; i < candles.length; i++) {
    const p = prices[i]
    if (p == null) {
      streak = 0
      continue
    }
    streak++
    if (streak < period) continue

    let sum = 0
    for (let j = i - period + 1; j <= i; j++) sum += prices[j]
    const middle = sum / period
    let sq = 0
    for (let j = i - period + 1; j <= i; j++) {
      const d = prices[j] - middle
      sq += d * d
    }
    const sd = Math.sqrt(sq / period)
    const upper = middle + mult * sd
    const lower = middle - mult * sd
    const bandwidth = middle !== 0 ? ((upper - lower) / middle) * 100 : 0
    const close = closes[i]
    const percentB = close != null && upper > lower
      ? ((close - lower) / (upper - lower)) * 100
      : null
    const time = candles[i]?.time
    if (time == null) continue
    points.push({ time, upper, middle, lower, bandwidth, percentB, _i: i })
  }

  if (points.length === 0) return emptyResult()

  // ── Toques, eventos y tramos de squeeze (un solo pase temporal) ──────────
  let upperTouches = 0
  let lowerTouches = 0
  let lastEvent = null
  const squeezeSegments = []
  let segStart = null

  for (let k = 0; k < points.length; k++) {
    const pt = points[k]
    const candle = candles[pt._i]
    const high = num(candle?.high)
    const low = num(candle?.low)
    const isSqueeze = pt.bandwidth < cfg.squeezeThreshold

    // Toque: la mecha alcanza la banda definida en ese punto.
    if (high != null && high >= pt.upper) {
      upperTouches++
      lastEvent = { type: 'touch_upper', price: pt.upper, time: pt.time }
    }
    if (low != null && low <= pt.lower) {
      lowerTouches++
      lastEvent = { type: 'touch_lower', price: pt.lower, time: pt.time }
    }

    // Inicio de squeeze: primer punto de un tramo squeeze.
    if (isSqueeze) {
      if (segStart == null) {
        segStart = pt.time
        lastEvent = { type: 'squeeze_start', price: pt.middle, time: pt.time }
      }
    } else if (segStart != null) {
      squeezeSegments.push({ fromTime: segStart, toTime: points[k - 1].time })
      segStart = null
    }

    // Cruce de vuelta dentro: el cierre estaba fuera de las bandas en el punto
    // anterior y vuelve al interior en este.
    if (k > 0) {
      const prev = points[k - 1]
      const prevClose = closes[prev._i]
      const close = closes[pt._i]
      if (prevClose != null && close != null) {
        const wasOutside = prevClose > prev.upper || prevClose < prev.lower
        const isInside = close <= pt.upper && close >= pt.lower
        if (wasOutside && isInside) {
          lastEvent = { type: 'reentry_inside', price: close, time: pt.time }
        }
      }
    }
  }
  if (segStart != null) {
    squeezeSegments.push({ fromTime: segStart, toTime: points[points.length - 1].time })
  }

  // ── Estado y derivados actuales (último punto) ───────────────────────────
  const last = points[points.length - 1]
  const lastClose = closes[last._i]
  let state
  if (last.bandwidth < cfg.squeezeThreshold) state = 'squeeze'
  else if (lastClose != null && lastClose > last.upper) state = 'price_above_upper'
  else if (lastClose != null && lastClose < last.lower) state = 'price_below_lower'
  else state = 'price_inside'

  // El índice interno no forma parte del contrato público del result.
  const cleanPoints = points.map(({ _i, ...rest }) => rest)

  return {
    points: cleanPoints,
    state,
    bandwidth: last.bandwidth,
    percentB: last.percentB,
    upperTouches,
    lowerTouches,
    lastEvent,
    squeezeSegments,
  }
}
